/**
 * DateRangePicker — styled trigger button + dropdown panel for desde/hasta date range.
 *
 * Visual style matches DateNavPicker: a single button trigger that opens a
 * calendar-like dropdown panel. The trigger shows the selected date range inline,
 * with sky-blue active highlight when dates are set.
 *
 * Replaces FilterDateGroup in filter panels while keeping the same props interface.
 * Keeps the same "Desde – Hasta" label row above the trigger so it aligns visually
 * with other filter fields.
 *
 * Usage:
 *   <DateRangePicker dark={dark} desde={desde} hasta={hasta}
 *     onDesde={setDesde} onHasta={setHasta} />
 */
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { usePosicionOverlay } from './usePosicionOverlay'
import { CamposRango } from './CamposRango'

interface DateRangePickerProps {
  dark: boolean
  desde: string
  hasta: string
  onDesde: (v: string) => void
  onHasta: (v: string) => void
  /** Sin la fila de rótulos DESDE – HASTA de encima.
   *
   *  Para barras de filtros donde el nombre de cada campo va DENTRO del control
   *  y no encima: ahí esta fila sobresale por arriba y descuadra la línea. El
   *  propio botón ya dice «Desde – Hasta» cuando no hay rango puesto, así que
   *  no se pierde nada. Por defecto va puesta, que es como la esperan las
   *  pantallas que ya lo usan. */
  sinRotulos?: boolean
}

function fmtShort(iso: string): string {
  if (!iso) return '?'
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const [y, m, d] = parts
  return `${d}/${m}/${y?.slice(2) ?? y}`
}

const IcoCalendar = () => (
  <svg
    className="w-3.5 h-3.5 shrink-0"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="2.5" width="12" height="10.5" rx="1.5" />
    <path d="M1 6h12M4.5 1v3M9.5 1v3" />
  </svg>
)

const IcoChevron = ({ open }: { open: boolean }) => (
  <svg
    className={`w-2.5 h-2.5 shrink-0 ml-auto transition-transform ${open ? 'rotate-180' : ''}`}
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M2 3.5l3 3 3-3" />
  </svg>
)

export function DateRangePicker({
  dark,
  desde,
  hasta,
  onDesde,
  onHasta,
  sinRotulos = false,
}: DateRangePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  // El calendario va en un portal con posición fija: dentro del control era un
  // `absolute` y participaba del layout de su contenedor, así que en la rejilla
  // de filtros de reports (`overflow-x-auto`) la ensanchaba y obligaba a hacer
  // scroll dentro del panel de filtros para verlo.
  //
  // El ancla es el BOTÓN, no el envoltorio: el envoltorio incluye la fila de
  // rótulos y el panel quedaría descolgado del campo.
  //
  // 220 = el ancho literal del panel; 230 ≈ su alto real con el botón de
  // limpiar visible. Con esas medidas el hook decide bien si abre hacia abajo o
  // hacia arriba y por qué lado ancla.
  const pos = usePosicionOverlay(trigger, open, { anchoMaximo: 220, altoDeseado: 230 })

  // Cierre por clic fuera. Hay que mirar el panel aparte del control: vive en
  // `document.body`, fuera del árbol del componente, y si no pulsar dentro del
  // calendario lo cerraría.
  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || panel.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  const hasValue = !!desde || !!hasta

  // Trigger button style — btnNeutral base + sky active state
  const triggerCls = [
    'flex items-center gap-1.5 whitespace-nowrap w-full',
    'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer',
    hasValue
      ? (dark
          ? 'bg-sky-500/10 border-sky-500/50 text-sky-300'
          : 'bg-sky-50 border-sky-300 text-sky-700')
      : open
        ? (dark
            ? 'bg-white/10 border-white/15 text-slate-200'
            : 'bg-slate-100 border-slate-200 text-slate-600')
        : (dark
            ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
            : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 shadow-sm'),
  ].join(' ')

  // Trigger text — shows date range summary
  let triggerText: string
  if (!desde && !hasta) {
    triggerText = `${t('common.from')} – ${t('common.to')}`
  } else if (desde && hasta) {
    triggerText = `${fmtShort(desde)} – ${fmtShort(hasta)}`
  } else if (desde) {
    triggerText = `${t('common.from')} ${fmtShort(desde)}`
  } else {
    triggerText = `${t('common.to')} ${fmtShort(hasta)}`
  }

  // Popup styling
  const popBg   = dark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'

  return (
    <div className="flex flex-col gap-1">
      {/* Label row — same structure as FilterDateGroup for visual alignment */}
      {!sinRotulos && <div className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
        <span>{t('common.from')}</span>
        <span className="opacity-30">–</span>
        <span>{t('common.to')}</span>
      </div>}

      {/* Trigger + dropdown wrapper */}
      <div ref={ref} className="relative">
        <button ref={trigger} onClick={() => setOpen(v => !v)} className={triggerCls}>
          <IcoCalendar />
          <span className="flex-1 text-left">{triggerText}</span>
          <IcoChevron open={open} />
        </button>

        {/* Ya no hay rama aparte para móvil: el hook recorta contra el viewport
            (margen a ambos lados, `maxWidth` y volteo hacia arriba), que es lo
            que aquel `fixed top-[72px]` centrado imitaba a mano.
            No se pasa `minWidth`: el panel tiene un ancho propio de 220 px —lo
            que piden dos campos de fecha— y un `minWidth` igual al ancho del
            botón lo estiraría o lo estrecharía sin motivo. Sí se aplica
            `maxWidth`, que es lo que lo encoge en ventanas muy estrechas. */}
        {open && pos && createPortal(
          <div
            ref={panel}
            style={{ top: pos.top, left: pos.left, right: pos.right, maxWidth: pos.maxWidth, maxHeight: pos.maxHeight }}
            className={`fixed z-[61] overflow-y-auto scroll-thin rounded-xl border shadow-xl p-3 w-[220px] ${popBg}`}
          >
            <CamposRango
              dark={dark}
              desde={desde}
              hasta={hasta}
              onDesde={onDesde}
              onHasta={onHasta}
              onLimpiar={() => { onDesde(''); onHasta(''); setOpen(false) }}
            />
          </div>,
          document.body,
        )}
      </div>
    </div>
  )
}
